import React from "react";
import { Theme } from "@mui/material";
import { ChartTooltip } from "@components/Tooltip/ChartTooltip";
import { toCurrency, toPercent } from "@helpers";

const DOT_STYLE = {
  height: "8px",
  width: "8px",
  minWidth: "8px",
  borderRadius: "50%",
};

export type Point = {
  y: number;
  value: number;
  index: number;
  tooltipData: {
    amount: number;
    name: string;
    id: number;
  }[];
};

type Props = {
  point: Point;
  theme?: Theme;
  totalMessage: string;
  expandedIds?: number[];
  data?: {
    [key: string]: {
      amount: number;
      name: string;
    }[];
  };
};

export const TooltipTable = ({
  point,
  theme,
  totalMessage,
  expandedIds,
}: Props) => {
  if ((point?.tooltipData?.length ?? 0) === 0) return <></>;

  const sortedData = point.tooltipData.sort((a, b) => a.amount - b.amount);

  return (
    <ChartTooltip theme={theme} noPadding>
      <div
        style={{
          width: "250px",
          maxHeight: "270px",
          whiteSpace: "normal",
          padding: "8px",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            fontSize: "10px",
            fontWeight: "600",
            lineHeight: theme?.typography.caption.lineHeight,
            color: theme?.palette.text.tertiary,
            marginBottom: "8px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <div
              style={{
                ...DOT_STYLE,
                backgroundColor: theme?.palette.graphVisual["01"].mid,
              }}
            />
            {new Date().getFullYear()}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            maxHeight: "245px",
            overflow: "auto",
            scrollbarWidth: "none",
          }}
        >
          {sortedData.map((item, index) => {
            const cutoffLength = 33;
            const isLong = item.name.length > cutoffLength;
            const isExpanded = expandedIds?.includes(item.id);
            const title =
              isLong && !isExpanded
                ? item.name.slice(0, cutoffLength)
                : item.name;
            return (
              <div
                key={item.name}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "4px",
                  paddingBottom:
                    index === point.tooltipData.length - 1 ? "57px" : "0",
                }}
              >
                <div
                  style={{
                    fontSize: theme?.typography.caption.fontSize,
                    lineHeight: theme?.typography.caption.lineHeight,
                    color: theme?.palette.text.onTooltipPrimary,
                    fontWeight: "600",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    wordBreak: "break-all",
                    gap: "4px",
                    WebkitLineClamp: isExpanded ? "unset" : 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  <div
                    style={{
                      ...DOT_STYLE,
                      backgroundColor: theme?.palette.graphVisual["01"].subtle,
                    }}
                  />
                  <span>
                    {title}
                    {!isExpanded && (
                      <span
                        style={{
                          cursor: "pointer",
                          color: theme?.palette.primary.main,
                          wordBreak: "normal",
                        }}
                        id={item.id?.toString()}
                        className="tooltip-table-text-expand"
                      >
                        {" ..."}
                      </span>
                    )}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "end",
                  }}
                >
                  <div
                    style={{
                      color: theme?.palette.text.onTooltipPrimary,
                      fontSize: theme?.typography.body3.fontSize,
                      lineHeight: "22px",
                      fontWeight: "600",
                      marginBottom: "4px",
                    }}
                  >
                    {toCurrency(item.amount, "EUR")}
                  </div>
                  <div
                    style={{
                      fontWeight: "600",
                      fontSize: theme?.typography.caption.fontSize,
                      lineHeight: theme?.typography.caption.lineHeight,
                      color: theme?.palette.text.onTooltipPrimarySub,
                    }}
                  >
                    {toPercent(
                      Math.abs(item.amount) / Math.abs(point.value),
                      undefined,
                      0,
                      0
                    ).replace(/\s/, "")}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div
        style={{
          padding: "8px",
          border: `1px solid ${theme?.palette.border.chipSecondaryDefault}`,
          color: theme?.palette.text.onTooltipPrimary,
          backgroundColor: theme?.palette.background.tooltipPrimary,
          fontSize: theme?.typography.caption.fontSize,
          fontWeight: "600",
          lineHeight: "16px",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          position: "absolute",
          bottom: "8px",
          left: "8px",
          width: "calc(100% - 16px)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <span>{totalMessage}</span>
          <span>{toCurrency(point.value, "EUR")}</span>
        </div>
        <span style={{ color: theme?.palette.text.onTooltipPrimarySub }}>
          100%
        </span>
      </div>
    </ChartTooltip>
  );
};
